import { GAME_CLEAR_ALL, GAME_PATH, GAME_FIND } from '../game/MinesweeperGame.js';

export const TYPE_ICONS = {
    [ GAME_CLEAR_ALL ]: '\u{1F4A3}',
    [ GAME_FIND ]:      '\u{1F48E}',
    [ GAME_PATH ]:      '\u{1F9ED}',
};

// Themed name pools per difficulty tier
const BEGINNER_NAMES = [
    'First Steps', 'Easy Breeze', 'Warm Up', 'Baby Steps', 'Soft Landing',
    'Clear Skies', 'Calm Waters', 'Gentle Start', 'Morning Dew', 'Light Touch',
    'Simple Path', 'Open Field', 'Daydream', 'Quiet Meadow', 'Sunrise',
    'Smooth Sailing', 'Fledgling', 'Sandbox', 'Early Bird', 'Low Tide',
    'Pebble Beach', 'Spring Breeze', 'Green Light', 'Safe Haven', 'Slow Roll',
];

const INTERMEDIATE_NAMES = [
    'Stepping Up', 'New Tricks', 'Hidden Gems', 'Rough Edges', 'Tricky Terrain',
    'Uncharted', 'Shifting Sands', 'Crossroads', 'Fog of War', 'Double Take',
    'Rising Tide', 'Growing Pains', 'Maze Runner', 'Trail Mix', 'Jagged Edge',
    'Detour', 'Winding Road', 'Side Quest', 'Puzzle Box', 'Riddle Me',
    'Quick Sand', 'Blind Spot', 'Tight Squeeze', 'Switchback', 'Minefield',
];

const ADVANCED_NAMES = [
    'Danger Zone', 'Deep Dive', 'Risky Business', 'No Margin', 'Mined Out',
    'Pressure Point', 'Steel Nerves', 'Razor Edge', 'High Stakes', 'Mine Shaft',
    'Volatile', 'Critical Mass', 'Checkmate', 'Dark Territory', 'Nail Biter',
    'Tunnel Vision', 'Tightrope', 'Overload', 'Fuse Box', 'Deadlock',
    'Iron Will', 'Hard Knock', 'Scorched', 'Vortex', 'Meltdown',
];

const EXPERT_NAMES = [
    'Clock Ticking', 'Time Crunch', 'Countdown', 'Rush Hour', 'Beat the Clock',
    'Under Pressure', 'Speed Demon', 'Last Chance', 'Final Hour', 'Crunch Time',
    'Warp Speed', 'Rapid Fire', 'Blitz', 'Flash Point', 'Zero Hour',
    'Overdrive', 'Time Bomb', 'Express Lane', 'Red Alert', 'Minute Mark',
    'Tempest', 'Firestorm', 'Shockwave', 'Fury', 'Havoc',
];

const MASTER_NAMES = [
    'Endgame', 'The Gauntlet', 'Grand Master', 'Final Boss', 'No Mercy',
    'Ultimate Test', 'Last Stand', 'Apex', 'Omega', 'The Summit',
    'Point of No Return', 'Crucible', 'Inferno', 'Oblivion', 'Total Recall',
    'Overdose', 'Dead End', 'Nemesis', 'Judgement Day', 'Armageddon',
    'Annihilation', 'Abyss', 'Ragnarok', 'Cataclysm', 'Extinction',
];

// Milestone hints — introduced at the level where each mechanic first appears
const HINTS = {
    1:   'Tap to reveal cells. Avoid the mines!',
    16:  'New mode! Arrows point toward the hidden diamond.',
    31:  'New mode! Find a safe path from bottom to top.',
    51:  'Time is ticking! Complete the board before the countdown hits zero.',
    100: 'Watch the edges! Blocked cells narrow your playing field.',
};

// Type-specific fallback hints — short, slightly sarcastic, mode-flavored.
// Picked deterministically per level (via seed) so a level always shows the same hint.
const TYPE_HINTS = {
    [ GAME_CLEAR_ALL ]: [
        "Don't step on a mine!",
        'Reveal everything that isn\'t trying to kill you.',
        'Clear the board. Skip the spicy squares.',
        'Every cell hides something. Most aren\'t lethal.',
        'Sweep the field. Mines are not optional.',
        'Find every safe square. Your toes will thank you.',
        'The board is mostly safe. Mostly.',
        'One wrong tap and it\'s confetti time.',
        'Reveal all the cells. Keep all your fingers.',
    ],
    [ GAME_FIND ]: [
        'Follow the arrows. Find the diamond. Don\'t die.',
        'Numbers point the way. Mines do not.',
        'Arrows know more than you. Trust them.',
        'The diamond is hiding. The mines are not.',
        'Hunt the gem. Dodge the ka-boom.',
        'Diamonds are forever. Limbs, less so.',
        'Let the arrows guide you. Let the mines... not.',
        'Treasure awaits — so do explosives.',
    ],
    [ GAME_PATH ]: [
        'Bottom to top. Without becoming a crater.',
        'Carve a path. Mines are not stepping stones.',
        'One safe route up. Ninety-nine ways to regret it.',
        'Find a way up. Preferably without fireworks.',
        'Connect bottom to top. Skip the explosive shortcuts.',
        'A clear path north. No detours through mines.',
        'Go up. Stay intact. In that order.',
        'Path the board, not your obituary.',
    ],
};

function pickHintForLevel( level )
{
    if ( !level ) return TYPE_HINTS[ GAME_CLEAR_ALL ][ 0 ];
    if ( level.hint ) return level.hint;
    const pool = TYPE_HINTS[ level.type ] || TYPE_HINTS[ GAME_CLEAR_ALL ];
    const idx = ( level.seed || level.levelNumber || 0 ) % pool.length;
    return pool[ idx ];
}

export function getHintForLevel( level )
{
    return pickHintForLevel( level );
}

function lerp( a, b, t )
{
    return Math.round( a + ( b - a ) * t );
}

function pickName( pool, index )
{
    return pool[ index % pool.length ];
}

function getBeginnerType( n )
{
    if ( n <= 15 ) return GAME_CLEAR_ALL;
    if ( n === 16 ) return GAME_FIND;       // introduction level
    if ( n <= 30 )
    {
        return ( n % 2 === 0 ) ? GAME_FIND : GAME_CLEAR_ALL;
    }
    if ( n === 31 ) return GAME_PATH;       // introduction level
    // 32-50: cycle all three
    const types = [ GAME_CLEAR_ALL, GAME_FIND, GAME_PATH ];
    return types[ ( n - 32 ) % 3 ];
}

function generateLevels()
{
    const levels = [];
    const ALL_TYPES = [ GAME_CLEAR_ALL, GAME_FIND, GAME_PATH ];

    for ( let n = 1; n <= 999; n++ )
    {
        let level;

        if ( n <= 50 )
        {
            // Beginner (1-50): 7x10 -> 8x12, 3-11 mines, no edge deco
            // Modes introduced: CLEAR_ALL (1), FIND (16), PATH (31)
            const t = ( n - 1 ) / 49;
            const w = lerp( 7, 8, t );
            const h = lerp( 10, 12, t );
            const mines = lerp( 3, 11, t );
            level = {
                levelNumber: n,
                seed: 10000 + n,
                name: pickName( BEGINNER_NAMES, n - 1 ),
                type: getBeginnerType( n ),
                difficulty: 'Beginner',
                width: w,
                height: h,
                numMines: mines,
                edgeDecorationLevel: 0,
                hint: HINTS[ n ] || null,
            };
        }
        else if ( n <= 250 )
        {
            // Intermediate (51-250): 8x12 -> 10x20, EdgeL from level 100
            const t = ( n - 51 ) / 199;
            const w = lerp( 8, 10, t );
            const h = lerp( 12, 20, t );
            const density = 0.12 + 0.05 * t; // 12% -> 17%
            const mines = Math.max( 12, Math.round( w * h * density ) );
            const edgeDeco = n >= 100 ? 1 : 0;
            level = {
                levelNumber: n,
                seed: 10000 + n,
                name: pickName( INTERMEDIATE_NAMES, n - 51 ),
                type: ALL_TYPES[ ( n - 51 ) % 3 ],
                difficulty: 'Intermediate',
                width: w,
                height: h,
                numMines: mines,
                edgeDecorationLevel: edgeDeco,
                hint: HINTS[ n ] || null,
            };
        }
        else if ( n <= 500 )
        {
            // Advanced (251-500): 10x20 -> 11x28, EdgeL 1->2
            const t = ( n - 251 ) / 249;
            const w = lerp( 10, 11, t );
            const h = lerp( 20, 28, t );
            const density = 0.16 + 0.03 * t; // 16% -> 19%
            const mines = Math.max( 30, Math.round( w * h * density ) );
            const edgeDeco = t < 0.5 ? 1 : 2;
            level = {
                levelNumber: n,
                seed: 10000 + n,
                name: pickName( ADVANCED_NAMES, n - 251 ),
                type: ALL_TYPES[ ( n - 251 ) % 3 ],
                difficulty: 'Advanced',
                width: w,
                height: h,
                numMines: mines,
                edgeDecorationLevel: edgeDeco,
                hint: HINTS[ n ] || null,
            };
        }
        else if ( n <= 800 )
        {
            // Expert (501-800): 11x28 -> 12x35, EdgeL 2->3
            const t = ( n - 501 ) / 299;
            const w = lerp( 11, 12, t );
            const h = lerp( 28, 35, t );
            const density = 0.18 + 0.03 * t; // 18% -> 21%
            const mines = Math.max( 50, Math.round( w * h * density ) );
            const edgeDeco = t < 0.5 ? 2 : 3;
            level = {
                levelNumber: n,
                seed: 10000 + n,
                name: pickName( EXPERT_NAMES, n - 501 ),
                type: ALL_TYPES[ ( n - 501 ) % 3 ],
                difficulty: 'Expert',
                width: w,
                height: h,
                numMines: mines,
                edgeDecorationLevel: edgeDeco,
                hint: HINTS[ n ] || null,
            };
        }
        else
        {
            // Master (801-999): 12x35 -> 12x40, EdgeL 3
            const t = ( n - 801 ) / 198;
            const w = 12;
            const h = lerp( 35, 40, t );
            const density = 0.20 + 0.04 * t; // 20% -> 24%
            const mines = Math.max( 80, Math.round( w * h * density ) );
            level = {
                levelNumber: n,
                seed: 10000 + n,
                name: pickName( MASTER_NAMES, n - 801 ),
                type: ALL_TYPES[ ( n - 801 ) % 3 ],
                difficulty: 'Master',
                width: w,
                height: h,
                numMines: mines,
                edgeDecorationLevel: 3,
                hint: HINTS[ n ] || null,
            };
        }

        levels.push( level );
    }

    return levels;
}

export const LEVELS = generateLevels();

export function getLevelByNumber( n )
{
    if ( n < 1 || n > LEVELS.length ) return null;
    return LEVELS[ n - 1 ];
}
